import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  Paper,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  EmojiEvents,
  Group,
  Gamepad,
  Code,
  Brush,
  Analytics,
  Speed,
  Security,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Home = () => {
  const navigate = useNavigate();

  const stats = [
    { value: '2', label: 'Games In Dev', subtitle: 'Coming Soon' },
    { value: '1', label: 'App Released', subtitle: 'Expense Tracker' },
    { value: '6', label: 'Team Members', subtitle: 'Passionate & Dedicated' },
    { value: '2025', label: 'Founded', subtitle: 'Bahawalpur, Pakistan' },
  ];

  const services = [
    {
      icon: <Gamepad sx={{ fontSize: 48 }} />,
      title: 'Unity Game Development',
      description: 'Expert mobile game development using Unity engine with engaging gameplay mechanics.',
    },
    {
      icon: <Code sx={{ fontSize: 48 }} />,
      title: 'Flutter App Development',
      description: 'Cross-platform mobile applications with beautiful, responsive designs using Flutter.',
    },
    {
      icon: <Brush sx={{ fontSize: 48 }} />,
      title: 'UI/UX Design',
      description: 'Creating intuitive and engaging user interfaces that provide exceptional user experiences.',
    },
    {
      icon: <Analytics sx={{ fontSize: 48 }} />,
      title: 'Full-Stack Development',
      description: 'Complete web and mobile solutions from backend architecture to frontend implementation.',
    },
    {
      icon: <Speed sx={{ fontSize: 48 }} />,
      title: 'AI Integration',
      description: 'Implementing artificial intelligence features to enhance gameplay and user interaction.',
    },
    {
      icon: <Security sx={{ fontSize: 48 }} />,
      title: 'C# Development',
      description: 'Professional C# programming for Unity games and backend systems.',
    },
  ];

  const technologies = [
    'Unity 2023',
    'Flutter',
    'C# / .NET',
    'Firebase',
    'Google Play Console',
    'Git / GitHub',
    'Android Studio',
    'Visual Studio',
  ];

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero Section */}
      <Box sx={{ 
        py: { xs: 10, md: 14 }, 
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(55, 65, 81, 0.08) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}>
        <Container maxWidth="lg">
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={6}
            alignItems="center"
          >
            <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                <Chip 
                  label="🚀 Game & App Development Startup | Est. 2025"
                  sx={{ 
                    mb: 3, 
                    py: 2.5,
                    px: 1,
                    bgcolor: 'rgba(55, 65, 81, 0.06)', 
                    color: '#374151', 
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    border: '1px solid rgba(55, 65, 81, 0.1)',
                    borderRadius: '50px',
                    '& .MuiChip-label': {
                      px: 2,
                    },
                  }}
                />
                <Typography 
                  variant="h1" 
                  component="h1" 
                  gutterBottom
                  sx={{
                    fontSize: { xs: '2.75rem', sm: '3.5rem', md: '4.25rem' },
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #1F2937 0%, #374151 50%, #4B5563 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    lineHeight: 1.1,
                    letterSpacing: '-1.5px',
                    mb: 3,
                  }}
                >
                  Mobile Games & Apps Built with Passion
                </Typography>
                <Typography 
                  variant="h6" 
                  color="text.secondary" 
                  paragraph
                  sx={{ 
                    mb: 5, 
                    lineHeight: 1.8,
                    fontSize: { xs: '1rem', md: '1.15rem' },
                    maxWidth: 560,
                    color: '#6B7280',
                  }}
                >
                  Based in Bahawalpur, Pakistan, we're creating exciting mobile games and apps using Unity and Flutter. From monster trucks to utility apps, we bring fun and useful experiences to users worldwide.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/games')}
                    sx={{
                      background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                      color: '#FFFFFF',
                      borderRadius: '14px',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 28px rgba(31,41,55,0.35)',
                      },
                      py: 1.75,
                      px: 4.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      boxShadow: '0 8px 24px rgba(31,41,55,0.25)',
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    }}
                  >
                    View Portfolio
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/contact')}
                    sx={{ 
                      py: 1.75, 
                      px: 4.5,
                      borderRadius: '14px',
                      background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
                      color: '#FFFFFF',
                      fontSize: '1rem',
                      fontWeight: 600,
                      boxShadow: '0 8px 24px rgba(31,41,55,0.25)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 28px rgba(31,41,55,0.35)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    }}
                  >
                    Start Your Project
                  </Button>
                </Stack>
              </motion.div>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: '#FFFFFF' }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, 
            gap: 4,
            p: { xs: 3, md: 5 },
            borderRadius: '24px',
            background: 'linear-gradient(135deg, rgba(55, 65, 81, 0.03) 0%, rgba(31, 41, 55, 0.06) 100%)',
            border: '1px solid rgba(55, 65, 81, 0.08)',
          }}>
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box sx={{ 
                  textAlign: 'center',
                  p: 2,
                  borderRadius: '16px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    transform: 'translateY(-4px)',
                  },
                }}>
                  <Typography 
                    variant="h2" 
                    sx={{ 
                      fontWeight: 800,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#374151', fontWeight: 600, mb: 0.5 }}>
                    {stat.label}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500 }}>
                    {stat.subtitle}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 12, position: 'relative' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#6B7280', 
                  fontWeight: 600, 
                  letterSpacing: 2,
                  mb: 2,
                  display: 'block',
                }}
              >
                WHAT WE OFFER
              </Typography>
              <Typography 
                variant="h2" 
                gutterBottom
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Our Services
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto', color: '#6B7280', fontWeight: 400 }}
              >
                Cutting-edge solutions for next-generation gaming platforms
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 4 }}>
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '20px',
                      transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                      border: '1px solid rgba(55, 65, 81, 0.08)',
                      overflow: 'visible',
                      '&:hover': {
                        transform: 'translateY(-12px)',
                        boxShadow: '0 25px 50px rgba(55, 65, 81, 0.12)',
                        borderColor: 'transparent',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: 'center' }}>
                      <Box sx={{ 
                        color: '#374151', 
                        mb: 3,
                        '& svg': {
                          fontSize: 52,
                        },
                      }}>
                        {service.icon}
                      </Box>
                      <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: '#1F2937' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#6B7280', lineHeight: 1.7 }}>
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Technology Stack */}
      <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container>
          <Typography 
            variant="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Our Tech Stack
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 6 }}
          >
            Built with modern, scalable, and future-proof technologies
          </Typography>
          <Stack 
            direction="row" 
            flexWrap="wrap" 
            justifyContent="center" 
            gap={2}
          >
            {technologies.map((tech, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <Chip
                  label={tech}
                  sx={{
                    py: 2.5,
                    px: 1,
                    fontSize: '1rem',
                    fontWeight: 600,
                    bgcolor: 'background.default',
                    border: '2px solid',
                    borderColor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.main',
                      color: '#FFFFFF',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all 0.3s',
                  }}
                />
              </motion.div>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Why Choose Us */}
      <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container>
          <Typography 
            variant="h2" 
            align="center" 
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 2 }}
          >
            Why Choose InnoVate?
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            sx={{ mb: 8 }}
          >
            The competitive advantages that set us apart in the gaming and app industry
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 4 }}>
            {[
              {
                icon: <EmojiEvents sx={{ fontSize: 48 }} />,
                title: 'Quality First',
                description: 'We focus on creating polished, engaging games and apps that users love and enjoy.',
              },
              {
                icon: <Group sx={{ fontSize: 48 }} />,
                title: 'Passionate Team',
                description: 'A dedicated team of 6 professionals committed to bringing creative ideas to life.',
              },
              {
                icon: <Speed sx={{ fontSize: 48 }} />,
                title: 'Modern Tech',
                description: 'Using Unity and Flutter to build cross-platform games and applications.',
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ py: 12 }}>
        <Container>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 4, md: 8 },
              textAlign: 'center',
              background: 'linear-gradient(135deg, #374151 0%, #1F2937 100%)',
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.1) 0%, transparent 60%)',
              },
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography 
                variant="h2" 
                gutterBottom 
                sx={{ 
                  color: 'white',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                Join the Gaming & App Revolution
              </Typography>
              <Typography variant="h5" sx={{ mb: 5, color: 'white', opacity: 0.95 }}>
                Investors, partners, and top talent: let's build the future together
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/contact')}
                  sx={{
                    background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
                    color: '#FFFFFF',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)',
                      transform: 'scale(1.05)',
                    },
                    py: 2,
                    px: 5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    transition: 'all 0.3s',
                  }}
                >
                  Start Your Project
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => window.open('mailto:investors@innovategames.com', '_blank')}
                  sx={{
                    borderColor: 'white',
                    color: 'white',
                    borderWidth: 2,
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      transform: 'scale(1.05)',
                    },
                    py: 2,
                    px: 5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    transition: 'all 0.3s',
                  }}
                >
                  Investor Deck
                </Button>
              </Stack>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default Home; 
